import React, { useState } from 'react';
import Papa from 'papaparse';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useSupabaseInventory } from '@/context/SupabaseInventoryContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useSales } from '@/hooks/useSales';
import { useWithdrawals } from '@/hooks/useWithdrawals';
import { useCompany } from '@/context/CompanyContext';
import { useManagementMode } from '@/hooks/useManagementMode';
import { supabase } from '@/integrations/supabase/client';
import { Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle, Eye, Crown } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
interface ImportPreview {
  valid: boolean;
  data: any[];
  cleanedData: any[];
  errors: string[];
  warnings: string[];
}
interface CleaningResult {
  original: string;
  cleaned: any;
  changed: boolean;
  error?: string;
}
export const ExportImportPanel: React.FC = () => {
  const {
    toast
  } = useToast();
  const {
    products,
    addProduct
  } = useSupabaseInventory();
  const { hasAccess, createCheckout, isLoading } = useSubscription();
  const {
    sales
  } = useSales();
  const {
    withdrawals
  } = useWithdrawals();
  const {
    userRole
  } = useCompany();
  const { isCaisseMode, isRetraitMode } = useManagementMode();
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [exportFormat, setExportFormat] = useState<'html' | 'csv'>('html');
  const [importResult, setImportResult] = useState<{
    imported: number;
    failed: number;
    importedProducts: string[];
    failedProducts: { name: string; error?: string }[];
    warnings: string[];
  } | null>(null);
  const [showImportResult, setShowImportResult] = useState(false);
  const isAdmin = userRole === 'admin';
  // Export des produits en CSV simple
  const exportProductsCSV = () => {
    if (products.length === 0) {
      toast({
        title: "Aucun produit",
        description: "Il n'y a aucun produit à exporter",
        variant: "destructive"
      });
      return;
    }
    const headers = ['Nom', 'Code-barres', 'Quantité', 'Prix', 'Seuil minimum'];
    const csvContent = [headers.join(','), ...products.map(product => [`"${product.name}"`, `"${product.barcode || ''}"`, product.quantity, product.price, product.min_threshold].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `produits_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Export CSV réussi",
      description: `${products.length} produits exportés en CSV`
    });
  };

  // Export des ventes en CSV simple
  const exportSalesCSV = () => {
    if (sales.length === 0) {
      toast({
        title: "Aucune vente",
        description: "Il n'y a aucune vente à exporter",
        variant: "destructive"
      });
      return;
    }
    const headers = ['Date', 'Produit', 'Quantité', 'Prix unitaire', 'Total', 'Vendeur'];
    const csvContent = [headers.join(','), ...sales.map(sale => [`"${new Date(sale.sale_date).toLocaleDateString('fr-FR')}"`, `"${sale.product_name}"`, sale.quantity_sold, sale.unit_price, sale.total_amount, `"${sale.seller_name || 'Non disponible'}"`].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ventes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Export CSV réussi",
      description: `${sales.length} ventes exportées en CSV`
    });
  };

  // Export des retraits en CSV simple
  const exportWithdrawalsCSV = () => {
    if (withdrawals.length === 0) {
      toast({
        title: "Aucun retrait",
        description: "Il n'y a aucun retrait à exporter",
        variant: "destructive"
      });
      return;
    }
    const headers = ['Date', 'Produit', 'Quantité', 'Raison', 'Vendeur'];
    const csvContent = [headers.join(','), ...withdrawals.map(withdrawal => [`"${new Date(withdrawal.withdrawal_date).toLocaleDateString('fr-FR')}"`, `"${withdrawal.product_name}"`, withdrawal.quantity_withdrawn, `"${withdrawal.reason || 'Retrait stock'}"`, `"${withdrawal.seller_name || 'Non disponible'}"`].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `retraits_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Export CSV réussi",
      description: `${withdrawals.length} retraits exportés en CSV`
    });
  };

  // Fonction principale d'export des produits
  const handleExportProducts = () => {
    if (exportFormat === 'csv') {
      exportProductsCSV();
    } else {
      exportProducts();
    }
  };

  // Fonction principale d'export des ventes
  const handleExportSales = () => {
    if (exportFormat === 'csv') {
      exportSalesCSV();
    } else {
      exportSales();
    }
  };

  // Fonction principale d'export des retraits
  const handleExportWithdrawals = () => {
    if (exportFormat === 'csv') {
      exportWithdrawalsCSV();
    } else {
      exportWithdrawals();
    }
  };

  // Fonctions de nettoyage des données
  const cleanNumericValue = (value: string, type: 'int' | 'float'): CleaningResult => {
    const original = value;

    // Supprimer tous les espaces (normaux, insécables, etc.)
    let cleaned = value.replace(/[\s\u00A0\u2000-\u200F\u2028-\u202F\u205F\u3000]/g, '');

    // Supprimer les symboles monétaires et caractères spéciaux
    cleaned = cleaned.replace(/[€$£¥₹₪₦₹₽₿₨₱₲₡₵₦₨₫₪₹₲₡₵₦₨₫₪]/g, '');
    cleaned = cleaned.replace(/['"«»]/g, '');

    // Remplacer virgules par points pour les décimales
    cleaned = cleaned.replace(/,/g, '.');

    // Supprimer les points multiples (garder seulement le dernier pour les décimales)
    if (type === 'float') {
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts.slice(0, -1).join('') + '.' + parts[parts.length - 1];
      }
    }
    const parsed = type === 'int' ? parseInt(cleaned) : parseFloat(cleaned);
    const isValid = !isNaN(parsed) && parsed >= 0;
    return {
      original,
      cleaned: isValid ? parsed : NaN,
      changed: original !== cleaned,
      error: !isValid ? `Valeur numérique invalide: "${original}"` : undefined
    };
  };
  const cleanTextValue = (value: string): CleaningResult => {
    const original = value;

    // Supprimer les espaces en début/fin
    let cleaned = value.trim();

    // Normaliser les espaces multiples
    cleaned = cleaned.replace(/\s+/g, ' ');

    // Supprimer les guillemets en début/fin
    cleaned = cleaned.replace(/^["']|["']$/g, '');

    // Optionnel: supprimer les accents si nécessaire (désactivé par défaut)
    // cleaned = cleaned.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    return {
      original,
      cleaned,
      changed: original !== cleaned
    };
  };

  // Export des produits en HTML stylé
  const exportProducts = () => {
    if (products.length === 0) {
      toast({
        title: "Aucun produit",
        description: "Il n'y a aucun produit à exporter",
        variant: "destructive"
      });
      return;
    }
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Export Produits - ${new Date().toLocaleDateString('fr-FR')}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 40px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      min-height: 100vh;
    }
    .container {
      background: white;
      border-radius: 20px;
      padding: 40px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      max-width: 1200px;
      margin: 0 auto;
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 30px;
      border-bottom: 3px solid #f1f5f9;
    }
    .title {
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #667eea, #764ba2);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin: 0 0 10px 0;
    }
    .subtitle {
      color: #64748b;
      font-size: 1.1rem;
      font-weight: 500;
    }
    .stats {
      display: flex;
      justify-content: center;
      gap: 30px;
      margin: 30px 0;
      flex-wrap: wrap;
    }
    .stat-card {
      background: linear-gradient(135deg, #f8fafc, #e2e8f0);
      padding: 20px 30px;
      border-radius: 12px;
      text-align: center;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    .stat-number {
      font-size: 2rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }
    .stat-label {
      color: #64748b;
      font-weight: 500;
      margin-top: 5px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 30px;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
    }
    thead {
      background: linear-gradient(135deg, #334155, #475569);
      color: white;
    }
    th {
      padding: 20px 16px;
      text-align: left;
      font-weight: 600;
      font-size: 0.95rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    tbody tr {
      background: white;
      transition: all 0.2s ease;
    }
    tbody tr:nth-child(even) {
      background: #f8fafc;
    }
    tbody tr:hover {
      background: #e0f2fe;
      transform: scale(1.01);
    }
    td {
      padding: 16px;
      border-bottom: 1px solid #e2e8f0;
      font-weight: 500;
    }
    .product-name {
      font-weight: 600;
      color: #1e293b;
      font-size: 1.05rem;
    }
    .barcode {
      font-family: 'Courier New', monospace;
      background: #f1f5f9;
      padding: 6px 10px;
      border-radius: 6px;
      color: #475569;
      font-size: 0.9rem;
    }
    .quantity {
      font-weight: 600;
      color: #0891b2;
      text-align: center;
    }
    .price {
      font-weight: 700;
      color: #059669;
      text-align: right;
      font-size: 1.1rem;
    }
    .threshold {
      font-weight: 600;
      color: #dc2626;
      text-align: center;
    }
    .footer {
      text-align: center;
      margin-top: 40px;
      padding-top: 30px;
      border-top: 2px solid #f1f5f9;
      color: #64748b;
      font-size: 0.9rem;
    }
    @media print {
      body { background: white; padding: 20px; }
      .container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 class="title">📦 Export des Produits</h1>
      <p class="subtitle">Inventaire complet au ${new Date().toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}</p>
      <div class="stats">
        <div class="stat-card">
          <div class="stat-number">${products.length}</div>
          <div class="stat-label">Produits total</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${products.reduce((sum, p) => sum + p.quantity, 0)}</div>
          <div class="stat-label">Quantité totale</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${products.reduce((sum, p) => sum + (p.quantity * p.price), 0).toFixed(2)}€</div>
          <div class="stat-label">Valeur stock</div>
        </div>
      </div>
    </div>
    
    <table>
      <thead>
        <tr>
          <th>🏷️ Nom du Produit</th>
          <th>📊 Code-barres</th>
          <th>📦 Quantité</th>
          <th>💰 Prix</th>
          <th>⚠️ Seuil Min</th>
        </tr>
      </thead>
      <tbody>
        ${products.map(product => `
          <tr>
            <td class="product-name">${product.name}</td>
            <td><span class="barcode">${product.barcode || 'N/A'}</span></td>
            <td class="quantity">${product.quantity}</td>
            <td class="price">${product.price.toFixed(2)}€</td>
            <td class="threshold">${product.min_threshold}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    
    <div class="footer">
      Généré automatiquement le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
    </div>
  </div>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], {
      type: 'text/html;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `produits_${new Date().toISOString().split('T')[0]}.html`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Export réussi",
      description: `${products.length} produits exportés en format stylé`
    });
  };

  // Export des ventes en HTML stylé
  const exportSales = () => {
    if (sales.length === 0) {
      toast({
        title: "Aucune vente",
        description: "Il n'y a aucune vente à exporter",
        variant: "destructive"
      });
      return;
    }
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Export Ventes - ${new Date().toLocaleDateString('fr-FR')}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 40px;
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      min-height: 100vh;
    }
    .container {
      background: white;
      border-radius: 20px;
      padding: 40px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      max-width: 1200px;
      margin: 0 auto;
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 30px;
      border-bottom: 3px solid #f1f5f9;
    }
    .title {
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #10b981, #059669);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin: 0 0 10px 0;
    }
    .subtitle {
      color: #64748b;
      font-size: 1.1rem;
      font-weight: 500;
    }
    .stats {
      display: flex;
      justify-content: center;
      gap: 30px;
      margin: 30px 0;
      flex-wrap: wrap;
    }
    .stat-card {
      background: linear-gradient(135deg, #f0fdf4, #dcfce7);
      padding: 20px 30px;
      border-radius: 12px;
      text-align: center;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    .stat-number {
      font-size: 2rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }
    .stat-label {
      color: #64748b;
      font-weight: 500;
      margin-top: 5px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 30px;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
    }
    thead {
      background: linear-gradient(135deg, #059669, #047857);
      color: white;
    }
    th {
      padding: 20px 16px;
      text-align: left;
      font-weight: 600;
      font-size: 0.95rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    tbody tr {
      background: white;
      transition: all 0.2s ease;
    }
    tbody tr:nth-child(even) {
      background: #f0fdf4;
    }
    tbody tr:hover {
      background: #dcfce7;
      transform: scale(1.01);
    }
    td {
      padding: 16px;
      border-bottom: 1px solid #e2e8f0;
      font-weight: 500;
    }
    .date {
      font-weight: 600;
      color: #1e293b;
    }
    .product-name {
      font-weight: 600;
      color: #374151;
    }
    .quantity {
      font-weight: 600;
      color: #0891b2;
      text-align: center;
    }
    .price {
      font-weight: 700;
      color: #059669;
      text-align: right;
    }
    .total {
      font-weight: 700;
      color: #dc2626;
      text-align: right;
      font-size: 1.1rem;
    }
    .seller {
      font-weight: 500;
      color: #6366f1;
    }
    .footer {
      text-align: center;
      margin-top: 40px;
      padding-top: 30px;
      border-top: 2px solid #f1f5f9;
      color: #64748b;
      font-size: 0.9rem;
    }
    @media print {
      body { background: white; padding: 20px; }
      .container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 class="title">💰 Export des Ventes</h1>
      <p class="subtitle">Historique des ventes au ${new Date().toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}</p>
      <div class="stats">
        <div class="stat-card">
          <div class="stat-number">${sales.length}</div>
          <div class="stat-label">Ventes total</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${sales.reduce((sum, s) => sum + s.quantity_sold, 0)}</div>
          <div class="stat-label">Articles vendus</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${sales.reduce((sum, s) => sum + s.total_amount, 0).toFixed(2)}€</div>
          <div class="stat-label">Chiffre d'affaires</div>
        </div>
      </div>
    </div>
    
    <table>
      <thead>
        <tr>
          <th>📅 Date</th>
          <th>🏷️ Produit</th>
          <th>📦 Quantité</th>
          <th>💵 Prix Unitaire</th>
          <th>💰 Total</th>
          <th>👤 Vendeur</th>
        </tr>
      </thead>
      <tbody>
        ${sales.map(sale => `
          <tr>
            <td class="date">${new Date(sale.sale_date).toLocaleDateString('fr-FR')}</td>
            <td class="product-name">${sale.product_name}</td>
            <td class="quantity">${sale.quantity_sold}</td>
            <td class="price">${sale.unit_price.toFixed(2)}€</td>
            <td class="total">${sale.total_amount.toFixed(2)}€</td>
            <td class="seller">${sale.seller_name || 'Non disponible'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    
    <div class="footer">
      Généré automatiquement le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
    </div>
  </div>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], {
      type: 'text/html;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `ventes_${new Date().toISOString().split('T')[0]}.html`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Export réussi",
      description: `${sales.length} ventes exportées en format stylé`
    });
  };

  // Export des retraits en HTML stylé
  const exportWithdrawals = () => {
    if (withdrawals.length === 0) {
      toast({
        title: "Aucun retrait",
        description: "Il n'y a aucun retrait à exporter",
        variant: "destructive"
      });
      return;
    }
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Export Retraits - ${new Date().toLocaleDateString('fr-FR')}</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 40px;
      background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
      min-height: 100vh;
    }
    .container {
      background: white;
      border-radius: 20px;
      padding: 40px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      max-width: 1200px;
      margin: 0 auto;
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 30px;
      border-bottom: 3px solid #f1f5f9;
    }
    .title {
      font-size: 2.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #f97316, #ea580c);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin: 0 0 10px 0;
    }
    .subtitle {
      color: #64748b;
      font-size: 1.1rem;
      font-weight: 500;
    }
    .stats {
      display: flex;
      justify-content: center;
      gap: 30px;
      margin: 30px 0;
      flex-wrap: wrap;
    }
    .stat-card {
      background: linear-gradient(135deg, #fed7aa, #fdba74);
      padding: 20px 30px;
      border-radius: 12px;
      text-align: center;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    .stat-number {
      font-size: 2rem;
      font-weight: 700;
      color: #1e293b;
      margin: 0;
    }
    .stat-label {
      color: #64748b;
      font-weight: 500;
      margin-top: 5px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 30px;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
    }
    thead {
      background: linear-gradient(135deg, #ea580c, #dc2626);
      color: white;
    }
    th {
      padding: 20px 16px;
      text-align: left;
      font-weight: 600;
      font-size: 0.95rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    tbody tr {
      background: white;
      transition: all 0.2s ease;
    }
    tbody tr:nth-child(even) {
      background: #fed7aa;
    }
    tbody tr:hover {
      background: #fdba74;
      transform: scale(1.01);
    }
    td {
      padding: 16px;
      border-bottom: 1px solid #e2e8f0;
      font-weight: 500;
    }
    .date {
      font-weight: 600;
      color: #1e293b;
    }
    .product-name {
      font-weight: 600;
      color: #374151;
    }
    .quantity {
      font-weight: 600;
      color: #dc2626;
      text-align: center;
    }
    .reason {
      font-weight: 500;
      color: #6366f1;
    }
    .seller {
      font-weight: 500;
      color: #f59e0b;
    }
    .footer {
      text-align: center;
      margin-top: 40px;
      padding-top: 30px;
      border-top: 2px solid #f1f5f9;
      color: #64748b;
      font-size: 0.9rem;
    }
    @media print {
      body { background: white; padding: 20px; }
      .container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 class="title">📤 Export des Retraits</h1>
      <p class="subtitle">Historique des retraits au ${new Date().toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}</p>
      <div class="stats">
        <div class="stat-card">
          <div class="stat-number">${withdrawals.length}</div>
          <div class="stat-label">Retraits total</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${withdrawals.reduce((sum, w) => sum + w.quantity_withdrawn, 0)}</div>
          <div class="stat-label">Articles retirés</div>
        </div>
        <div class="stat-card">
          <div class="stat-number">${new Set(withdrawals.map(w => w.product_id)).size}</div>
          <div class="stat-label">Produits concernés</div>
        </div>
      </div>
    </div>
    
    <table>
      <thead>
        <tr>
          <th>📅 Date</th>
          <th>🏷️ Produit</th>
          <th>📦 Quantité</th>
          <th>📝 Raison</th>
          <th>👤 Vendeur</th>
        </tr>
      </thead>
      <tbody>
        ${withdrawals.map(withdrawal => `
          <tr>
            <td class="date">${new Date(withdrawal.withdrawal_date).toLocaleDateString('fr-FR')}</td>
            <td class="product-name">${withdrawal.product_name}</td>
            <td class="quantity">${withdrawal.quantity_withdrawn}</td>
            <td class="reason">${withdrawal.reason || 'Retrait stock'}</td>
            <td class="seller">${withdrawal.seller_name || 'Non disponible'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    
    <div class="footer">
      Généré automatiquement le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
    </div>
  </div>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], {
      type: 'text/html;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `retraits_${new Date().toISOString().split('T')[0]}.html`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: "Export réussi",
      description: `${withdrawals.length} retraits exportés en format stylé`
    });
  };

  // Vérifier la limite avant l'import
  const checkImportLimit = (productsToImport: number): { allowed: boolean; maxAllowed?: number } => {
    if (hasAccess) return { allowed: true }; // Pas de limite pour les utilisateurs premium
    
    const currentProductCount = products.length;
    const maxProducts = 5;
    const remainingSlots = maxProducts - currentProductCount;
    
    if (remainingSlots <= 0) {
      setShowLimitAlert(true);
      return { allowed: false };
    }
    
    if (productsToImport > remainingSlots) {
      toast({
        title: "Limite d'import atteinte",
        description: `Vous ne pouvez importer que ${remainingSlots} produit(s) supplémentaire(s) avec votre compte démo.`,
        variant: "destructive"
      });
      return { allowed: false, maxAllowed: remainingSlots };
    }
    
    return { allowed: true };
  };

  // Prévisualisation du fichier CSV avec nettoyage intelligent utilisant PapaParse
  const previewImport = (file: File) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      delimiter: '',
      // Auto-detect , or ;
      encoding: 'UTF-8',
      complete: results => {
        try {
          if (!results.data || results.data.length < 2) {
            throw new Error('Le fichier doit contenir au moins une ligne d\'en-tête et une ligne de données');
          }

          // Parser les en-têtes
          const headerRow = results.data[0] as string[];
          const headers = headerRow.map(h => cleanTextValue(h || '').cleaned);
          const requiredHeaders = ['Nom', 'Code-barres', 'Quantité', 'Prix', 'Seuil minimum'];
          const errors: string[] = [];
          const warnings: string[] = [];
          const data: any[] = [];
          const cleanedData: any[] = [];

          // Vérifier les en-têtes
          const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
          if (missingHeaders.length > 0) {
            errors.push(`Colonnes manquantes: ${missingHeaders.join(', ')}`);
          }

          // Index des colonnes
          const nameIndex = headers.indexOf('Nom');
          const barcodeIndex = headers.indexOf('Code-barres');
          const quantityIndex = headers.indexOf('Quantité');
          const priceIndex = headers.indexOf('Prix');
          const thresholdIndex = headers.indexOf('Seuil minimum');

          // Traiter les données ligne par ligne
          for (let i = 1; i < results.data.length; i++) {
            const lineNumber = i + 1;
            const row = results.data[i] as string[];
            if (row.length !== headers.length) {
              errors.push(`Ligne ${lineNumber}: Nombre de colonnes incorrect (${row.length} au lieu de ${headers.length})`);
              continue;
            }

            // Nettoyer chaque champ
            const nameResult = cleanTextValue(row[nameIndex] || '');
            const barcodeResult = cleanTextValue(row[barcodeIndex] || '');
            const quantityResult = cleanNumericValue(row[quantityIndex] || '0', 'int');
            const priceResult = cleanNumericValue(row[priceIndex] || '0', 'float');
            const thresholdResult = cleanNumericValue(row[thresholdIndex] || '0', 'int');

            // Données originales
            const originalData = {
              name: nameResult.original,
              barcode: barcodeResult.original,
              quantity: row[quantityIndex],
              price: row[priceIndex],
              min_threshold: row[thresholdIndex]
            };

            // Données nettoyées
            const cleanedProductData = {
              name: nameResult.cleaned,
              barcode: barcodeResult.cleaned,
              quantity: quantityResult.cleaned,
              price: priceResult.cleaned,
              min_threshold: thresholdResult.cleaned
            };

            // Validation des champs obligatoires
            if (!nameResult.cleaned.trim()) {
              errors.push(`Ligne ${lineNumber}: Nom du produit manquant`);
            }

            // Validation des valeurs numériques
            if (quantityResult.error) {
              errors.push(`Ligne ${lineNumber}: ${quantityResult.error}`);
            }
            if (priceResult.error) {
              errors.push(`Ligne ${lineNumber}: ${priceResult.error}`);
            }
            if (thresholdResult.error) {
              errors.push(`Ligne ${lineNumber}: ${thresholdResult.error}`);
            }

            // Avertissements pour les changements
            const changes: string[] = [];
            if (nameResult.changed) changes.push(`nom: "${nameResult.original}" → "${nameResult.cleaned}"`);
            if (barcodeResult.changed) changes.push(`code-barres: "${barcodeResult.original}" → "${barcodeResult.cleaned}"`);
            if (quantityResult.changed) changes.push(`quantité: "${quantityResult.original}" → ${quantityResult.cleaned}`);
            if (priceResult.changed) changes.push(`prix: "${priceResult.original}" → ${priceResult.cleaned}`);
            if (thresholdResult.changed) changes.push(`seuil: "${thresholdResult.original}" → ${thresholdResult.cleaned}`);
            if (changes.length > 0) {
              warnings.push(`Ligne ${lineNumber}: Données nettoyées (${changes.join(', ')})`);
            }
            data.push(originalData);
            cleanedData.push(cleanedProductData);
          }
          // Vérifier la limite avant d'afficher la prévisualisation
          const limitCheck = checkImportLimit(cleanedData.length);
          if (!limitCheck.allowed) {
            return; // Arrêter si la limite est atteinte
          }

          setPreview({
            valid: errors.length === 0,
            data,
            cleanedData,
            errors,
            warnings
          });
          setShowPreview(true);
        } catch (error) {
          toast({
            title: "Erreur de parsing",
            description: "Impossible de parser le fichier CSV",
            variant: "destructive"
          });
        }
      },
      error: error => {
        toast({
          title: "Erreur de lecture",
          description: `Erreur lors de la lecture du fichier: ${error.message}`,
          variant: "destructive"
        });
      }
    });
  };

  // Import des produits depuis CSV avec données nettoyées
  const importProducts = async () => {
    if (!preview || !preview.valid) return;
    setImporting(true);
    try {
      let imported = 0;
      let failed = 0;
      const importedProducts: string[] = [];
      const failedProducts: { name: string; error?: string }[] = [];
      const enrichedFromOpenFood: string[] = [];

      for (const productData of preview.cleanedData) {
        try {
          let enrichedData = { ...productData };
          
          // Si le produit a un code-barres, essayer de l'enrichir avec OpenFoodFacts
          if (productData.barcode && productData.barcode.trim()) {
            try {
              const { data, error } = await supabase.functions.invoke('barcode-lookup', {
                body: { barcode: productData.barcode }
              });

              const offFound = data?.success && data?.found && data?.product;
              const offName = data?.product?.name as string | undefined;
              const offImage = data?.product?.image_url as string | undefined;

              if (!error && offFound) {
                // Mettre à jour le nom et l'image si trouvés
                if (offName) enrichedData.name = offName;
                if (offImage) enrichedData.image_url = offImage;
                enrichedFromOpenFood.push(productData.barcode);
              }
            } catch (lookupError) {
              // Continuer avec les données originales si la recherche échoue
              console.log(`Impossible d'enrichir le produit ${productData.barcode}:`, lookupError);
            }
          }

          const success = await addProduct(enrichedData);
          if (success) {
            imported++;
            importedProducts.push(enrichedData.name);
          } else {
            failed++;
            failedProducts.push({ name: productData.name, error: 'Échec de l\'ajout' });
          }
        } catch (error) {
          failed++;
          failedProducts.push({ 
            name: productData.name, 
            error: error instanceof Error ? error.message : 'Erreur inconnue'
          });
        }
      }

      // Ajouter info sur les enrichissements
      const enrichmentWarnings = enrichedFromOpenFood.length > 0 
        ? [`${enrichedFromOpenFood.length} produit(s) enrichi(s) depuis OpenFoodFacts`]
        : [];

      // Afficher le résultat détaillé
      setImportResult({
        imported,
        failed,
        importedProducts,
        failedProducts,
        warnings: [...(preview.warnings || []), ...enrichmentWarnings]
      });
      setShowImportResult(true);

      setPreview(null);
      setShowPreview(false);
    } catch (error) {
      toast({
        title: "Erreur d'import",
        description: "Impossible d'importer les produits",
        variant: "destructive"
      });
    } finally {
      setImporting(false);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/csv') {
      previewImport(file);
    } else {
      toast({
        title: "Format invalide",
        description: "Veuillez sélectionner un fichier CSV",
        variant: "destructive"
      });
    }
    e.target.value = '';
  };
  if (!isAdmin) {
    return <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            <FileSpreadsheet className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>Vous n'avez pas les permissions pour importer/exporter des données.</p>
          </div>
        </CardContent>
      </Card>;
  }
  return <div className="space-y-6">
      {/* Export */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500/10 to-blue-600/20 transition-transform duration-200 hover:scale-105">
              <Download className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1 text-blue-700 dark:text-blue-300">
                Data management
              </p>
              <p className="text-xl font-bold text-blue-800 dark:text-blue-200">
                Export de données
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Sélection du format d'export */}
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 block">
              Format d'export
            </Label>
            <div className="flex gap-3">
              <Button
                variant={exportFormat === 'html' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setExportFormat('html')}
                className="flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                HTML Stylé
              </Button>
              <Button
                variant={exportFormat === 'csv' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setExportFormat('csv')}
                className="flex items-center gap-2"
              >
                <FileSpreadsheet className="w-4 h-4" />
                CSV Simple
              </Button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
              {exportFormat === 'html' ? 
                '📄 Fichier HTML avec design professionnel et statistiques' : 
                '📊 Fichier CSV compatible avec Excel et autres tableurs'
              }
            </p>
          </div>

          <div className={`grid gap-4 ${
            // Adapter la grille selon le mode de gestion
            isCaisseMode ? 'grid-cols-1 md:grid-cols-2' : 
            isRetraitMode ? 'grid-cols-1 md:grid-cols-2' : 
            'grid-cols-1 md:grid-cols-3'
          }`}>
            <Button onClick={handleExportProducts} className="flex items-center gap-3 h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105">
              <FileSpreadsheet className="w-5 h-5" />
              Exporter les produits ({products.length})
            </Button>
            
            {/* Afficher le bouton ventes seulement en mode caisse ou sans mode défini */}
            {(!isCaisseMode && !isRetraitMode) || isCaisseMode ? (
              <Button onClick={handleExportSales} className="flex items-center gap-3 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105">
                <FileSpreadsheet className="w-5 h-5" />
                Exporter les ventes ({sales.length})
              </Button>
            ) : null}
            
            {/* Afficher le bouton retraits seulement en mode retrait ou sans mode défini */}
            {(!isCaisseMode && !isRetraitMode) || isRetraitMode ? (
              <Button onClick={handleExportWithdrawals} className="flex items-center gap-3 h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105">
                <FileSpreadsheet className="w-5 h-5" />
                Exporter les retraits ({withdrawals.length})
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Import */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-green-600/20 transition-transform duration-200 hover:scale-105">
              <Upload className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1 text-green-700 dark:text-green-300">
                Data management
              </p>
              <p className="text-xl font-bold text-green-800 dark:text-green-200">
                Import de produits
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="csvFile">Fichier CSV</Label>
            <Input id="csvFile" type="file" accept=".csv" onChange={handleFileSelect} />
            <p className="text-sm text-muted-foreground">
              Format requis: Nom, Code-barres, Quantité, Prix, Seuil minimum<br />
              
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Prévisualisation */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Aperçu de l'import
            </DialogTitle>
            <DialogDescription>
              Vérifiez les données nettoyées avant l'import final
            </DialogDescription>
          </DialogHeader>
          
          {preview && <div className="space-y-4">
               {/* Statut */}
               <div className="flex items-center gap-2">
                 {preview.valid ? <CheckCircle className="w-5 h-5 text-green-500" /> : <AlertTriangle className="w-5 h-5 text-red-500" />}
                 <span className={preview.valid ? 'text-green-600' : 'text-red-600'}>
                   {preview.valid ? 'Fichier valide' : 'Erreurs détectées'}
                 </span>
                 <span className="text-muted-foreground">
                   ({preview.cleanedData.length} produits)
                 </span>
                 {preview.warnings.length > 0 && <span className="text-orange-600 text-sm">
                     ({preview.warnings.length} nettoyages effectués)
                   </span>}
               </div>

               {/* Erreurs */}
               {preview.errors.length > 0 && <div className="bg-destructive/10 border border-destructive/20 rounded p-4">
                   <h4 className="font-medium text-destructive mb-2">Erreurs ({preview.errors.length}):</h4>
                   <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                     {preview.errors.map((error, i) => <li key={i} className="text-destructive">• {error}</li>)}
                   </ul>
                 </div>}

               {/* Avertissements de nettoyage */}
               {preview.warnings.length > 0 && <div className="bg-orange-50 border border-orange-200 rounded p-4">
                   <h4 className="font-medium text-orange-700 mb-2">Données nettoyées ({preview.warnings.length}):</h4>
                   <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                     {preview.warnings.slice(0, 5).map((warning, i) => <li key={i} className="text-orange-600">• {warning}</li>)}
                     {preview.warnings.length > 5 && <li className="text-orange-500 italic">... et {preview.warnings.length - 5} autres modifications</li>}
                   </ul>
                 </div>}

               {/* Aperçu des données nettoyées */}
               {preview.cleanedData.length > 0 && <div className="border rounded">
                   <div className="bg-muted px-3 py-2 border-b">
                     <h4 className="font-medium text-sm">Aperçu des données (après nettoyage)</h4>
                   </div>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm font-inter">
                        <thead className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800 dark:to-gray-800">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 tracking-wide">Nom</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 tracking-wide">Code-barres</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 tracking-wide">Quantité</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 tracking-wide">Prix</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300 tracking-wide">Seuil min</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700">
                          {preview.cleanedData.slice(0, 10).map((product, i) => {
                     const original = preview.data[i];
                     return <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors duration-200">
                                <td className="px-4 py-3">
                                  <span className={original?.name !== product.name ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 px-2 py-1 rounded-md font-medium' : 'text-slate-900 dark:text-slate-100 font-medium'}>
                                    {product.name}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={original?.barcode !== product.barcode ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 px-2 py-1 rounded-md font-mono text-sm' : 'text-slate-700 dark:text-slate-300 font-mono text-sm'}>
                                    {product.barcode}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={original?.quantity !== product.quantity.toString() ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 px-2 py-1 rounded-md font-semibold' : 'text-slate-700 dark:text-slate-300 font-semibold'}>
                                    {product.quantity}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={original?.price !== product.price.toString() ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 px-2 py-1 rounded-md font-bold' : 'text-emerald-600 dark:text-emerald-400 font-bold'}>
                                    {product.price}€
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={original?.min_threshold !== product.min_threshold.toString() ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 px-2 py-1 rounded-md font-medium' : 'text-amber-600 dark:text-amber-400 font-medium'}>
                                    {product.min_threshold}
                                  </span>
                                </td>
                              </tr>;
                   })}
                        </tbody>
                      </table>
                      {preview.cleanedData.length > 10 && <div className="p-4 text-sm text-slate-600 dark:text-slate-400 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800 dark:to-gray-800 font-medium">
                          ... et {preview.cleanedData.length - 10} autres produits
                        </div>}
                    </div>
                 </div>}

              {/* Actions */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Annuler
                </Button>
                 <Button onClick={importProducts} disabled={!preview.valid || importing} className="h-12 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105">
                    {importing ? 'Import en cours...' : `Importer ${preview.cleanedData.length} produits`}
                  </Button>
              </div>
            </div>}
        </DialogContent>
      </Dialog>

      {/* Alert Dialog pour limite atteinte */}
      <AlertDialog open={showLimitAlert} onOpenChange={setShowLimitAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              Limite atteinte
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Vous avez atteint la limite de 5 produits en mode démo. 
                L'importation ajouterait plus de produits que votre limite autorisée.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Produits actuels :</strong> {products.length}/5<br />
                  <strong>Avec Premium :</strong> Produits illimités
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-3">
            <AlertDialogAction 
              onClick={() => {
                setShowLimitAlert(false);
                createCheckout();
              }}
              disabled={isLoading}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
            >
              {isLoading ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Crown className="w-4 h-4 mr-2" />
              )}
              Passer à Premium
            </AlertDialogAction>
            <Button 
              onClick={() => setShowLimitAlert(false)}
              variant="outline"
            >
              Annuler
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog pour résultat d'import */}
      <Dialog open={showImportResult} onOpenChange={setShowImportResult}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Résultats de l'import
            </DialogTitle>
            <DialogDescription>
              Récapitulatif détaillé de l'importation
            </DialogDescription>
          </DialogHeader>
          
          {importResult && (
            <div className="space-y-6">
              {/* Statistiques */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
                  <div className="text-sm text-green-700">Produits importés</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{importResult.failed}</div>
                  <div className="text-sm text-red-700">Échecs d'import</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{importResult.warnings.length}</div>
                  <div className="text-sm text-orange-700">Données nettoyées</div>
                </div>
              </div>

              {/* Produits importés avec succès */}
              {importResult.imported > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Produits importés avec succès ({importResult.imported})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                    {importResult.importedProducts.map((name, i) => (
                      <div key={i} className="text-sm text-green-700 bg-green-100 px-3 py-1 rounded">
                        ✅ {name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Produits non importés */}
              {importResult.failed > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Produits non importés ({importResult.failed})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {importResult.failedProducts.map((product, i) => (
                      <div key={i} className="bg-red-100 border border-red-200 rounded p-3">
                        <div className="font-medium text-red-800">❌ {product.name}</div>
                        {product.error && (
                          <div className="text-sm text-red-600 mt-1">
                            Raison: {product.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Avertissements de nettoyage */}
              {importResult.warnings.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Données automatiquement nettoyées ({importResult.warnings.length})
                  </h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {importResult.warnings.slice(0, 10).map((warning, i) => (
                      <div key={i} className="text-sm text-orange-700">• {warning}</div>
                    ))}
                    {importResult.warnings.length > 10 && (
                      <div className="text-sm text-orange-600 italic">
                        ... et {importResult.warnings.length - 10} autres modifications
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action */}
              <div className="flex justify-end">
                <Button onClick={() => setShowImportResult(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>;
};